<template>
  <k-modal id="add-new-scope-modal" :show="showModal"
    @close="onClose"
    :closeOnClickAway="false"
  >
    <template #header>
      <h3>{{ modalTitle }}</h3>
    </template>
    <template #body>
      <div class="modal-body">
        <k-form :columns="10">
          <k-form-section :column-span="10">
            <k-form-field label="Project Name" :required="true" field-id="project-name-input">
              <input id="project-name-input" type="url" class="form-control" :readonly="editingDisabled" placeholder="Type Project name here..." v-model="projectName">
            </k-form-field>
            <k-form-field label="Project Description" :required="true" field-id="project-description-input">
              <textarea
                id="project-description-input"
                placeholder="Type description here..."
                v-model="projectDescription"
                rows="3"
                class="form-control"
                :maxlength="descMaxLength"
                :readonly="editingDisabled"
              />
              <k-character-count :value-length="projectDescription.length" :max-length="descMaxLength" />
            </k-form-field>
          </k-form-section>
          <k-form-section :column-span="10">
            <k-form-field label="Add scoping document">
              <div class="scope-upload-container">
                <div class="scope-mode-toggle">
                  <button
                    type="button"
                    class="btn btn-primary toggle-btn"
                    :class="{ active: isLinkMode }"
                    @click="setDocumentUploadMode('link')"
                    :disabled="editingDisabled"
                    aria-describedby="link-switch-description"
                  >
                    Link
                  </button>
                  <p class="sr-only" id="link-switch-description">Use external link as scoping document</p>
                  <button
                    type="button"
                    class="btn btn-primary toggle-btn"
                    :class="{ active: !isLinkMode }"
                    @click="setDocumentUploadMode('file')"
                    :disabled="editingDisabled"
                    aria-describedby="file-switch-description"
                  >
                    Upload
                  </button>
                </div>
                <p class="sr-only" id="file-switch-description">Upload file as scoping document</p>
                <button v-if="hasDocumentToView"
                  type="button"
                  @click="downloadScopingDocument"
                  class="btn btn-primary download-btn"
                >
                  <i class="fas fa-download" aria-hidden="true"></i>
                  Download current scoping document
                </button>
              </div>
              <div v-if="isLinkMode" class="project-link-wrapper">
                <p for="project-scope-input">You can also upload a file. Switch using the above buttons.</p>
                <span class="helper-text">Please save your scoping document link before submitting the form</span>
                <div v-if="!isEditingLink" class="scoping-link-view">
                  <div class="scoping-link-container">
                    <a :href="projectScopingDocument" target="_blank">{{ projectScopingDocument }}</a>
                  </div>
                  <button type="button" class="btn btn-outlined" @click="isEditingLink = true" :disabled="editingDisabled" title="Edit scoping document link" aria-label="Edit scoping document link">
                  <i class="fas fa-pencil-alt" aria-hidden="true"></i>
                  </button>
                </div>
                <div v-else class="scoping-link-edit">
                  <input class="form-control" type="url" v-model="projectScopingDocument" :readonly="editingDisabled" placeholder="Add scoping document link here..." />
                  <button type="button" class="btn btn-success" @click="isEditingLink = false" :disabled="editingDisabled" title="Save scoping document link" aria-label="Save scoping document link">
                    <i class="fas fa-check" aria-hidden="true"></i>
                  </button>
                </div>
                <span v-if="!isUrlValid && isEditingLink" class="helper-text">{{ urlValidationMessage }}</span>
              </div>
              <div v-else class="project-upload-wrapper">
                <p for="project-scope-input">You can also submit an external link as scoping document. Switch using the above buttons.</p>
                <p for="project-scope-input">If not stafisfied with current document, please upload a new one.</p>
                <dropfield @file="filesChange($event[0])"
                  ref="scopeUploadField"
                  :immediatePost="false"
                  :disableDropfield="editingDisabled || isUploading"
                  :accept="acceptedFileTypes"
                  url=""
                  :enablePreviews="true"
                  id="upload-scope-document"
                  :customMessage="dropfieldMessage"
                  :acceptedFilesInfo="acceptedFilesInfo"
                >
                </dropfield>
              </div>
            </k-form-field>
          </k-form-section>
          <k-form-section :column-span="10" legend="Competencies" :required="true">
            <small>Select Competencies relevant to the project</small>
            <template v-if="competenciesReady && competencies.length > 0">
              <k-editable-table
                v-model="competencies"
                :headers="tableHeaders"
                :max="10"
                :initial-sort-state="initialSortState"
              />
            </template>
            <div v-else>
              <span class="label-text">No competencies found for this programme</span>
            </div>
          </k-form-section>
        </k-form>
      </div>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="btn btn-outlined" @click="onClose">Cancel</button>
      </div>
      <div class="modal-footer-right">
        <button v-if="modalMode === 'edit'" class="btn btn-danger delete-project" @click="confirmDeleteProject" :disabled="!canDeleteProject || editingDisabled">Delete Project</button>
        <button class="btn btn-default save-draft" @click="saveOrEditDraft" :disabled="!validSaveAsDraft || editingDisabled">Save as Draft</button>
        <button class="btn btn-success submit" @click="submitForReview" :disabled="!validSubmitForReview || editingDisabled">Submit for Review</button>

      </div>
    </template>
  </k-modal>
</template>

<style>
#add-new-scope-modal .modal-footer {
  display: flex;
  justify-content: space-between;
}

#add-new-scope-modal .modal-container {
  width: 80vw;
}

#add-new-scope-modal .form-control[readonly],
#add-new-scope-modal textarea[readonly] {
  background-color: var(--input-background);
  border: var(--input-border);
  cursor: not-allowed;
}
</style>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

#upload-scope-document {
  width: 100%;
}

.scope-upload-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.scope-mode-toggle {
  display: flex;
  gap: 0;
  margin: 15px 0;
}

.scope-mode-toggle .toggle-btn {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-right: 1px solid var(--input-border);
  margin: 0;
  min-width: 120px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  background-color: var(--kate-button-primary);
  color: var(--kate-type-dark);
  border-color: var(--kate-button-primary);
}

.scope-mode-toggle .toggle-btn:first-child {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.scope-mode-toggle .toggle-btn:last-child {
  border-right: none;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

.scope-mode-toggle .toggle-btn:hover:not(:disabled):not(.active) {
  background-color: var(--kate-button-primary-hover);
  color: var(--kate-type-light);
}

[data-theme="light"] .scope-mode-toggle .toggle-btn.active:hover:not(:disabled),
[data-theme="light"] .scope-mode-toggle .toggle-btn.active:focus:not(:disabled) {
  color: var(--kate-type-dark) !important;
}

.scope-mode-toggle .toggle-btn.active {
  background-color: var(--kate-background);
  border-color: var(--kate-background);
  color: var(--kate-type-white);
}

.scope-mode-toggle .toggle-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.project-link-wrapper,
.project-upload-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 10px;
}

.project-link-wrapper > label,
.project-upload-wrapper > label {
  font-weight: 600;
  color: var(--kate-type-primary);
  margin-bottom: 5px;
}

.scoping-link-view,
.scoping-link-edit {
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: center;
}

.scoping-link-container {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--input-border);
  border-radius: 4px;
  background-color: var(--input-background);
  font-family: inherit;
  line-height: 1.5;
  min-height: 38px;
  display: flex;
  align-items: center;
}

.helper-text {
  font-size: 0.9em;
  color: var(--kate-type-secondary);
  margin: 0;
  font-style: italic;
}

#upload-scope-document :deep(.k-dropbox) > label {
  display: block;
  padding: 0;
  font-size: 1em;
}

#upload-scope-document :deep(.k-dropbox) {
  display: flex;
  min-height: 150px;
  height: auto;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

#upload-scope-document :deep(.k-dropbox .upload-text) {
  font-weight: 500;
  color: var(--kate-type-secondary);
}

#upload-scope-document :deep(.k-dropbox i) {
  margin-top: 12px;
  font-size: 20px;
  color: var(--kate-type-primary);
}

</style>

<script>
import KModal from '@base-components/k-modal.vue';
import KEditableTable from '@base-components/k-editable-table.vue';
import {
  KForm, KFormField, KFormSection, KCharacterCount,
} from '@base-components/forms';
import Dropfield from '@base-components/dropfield.vue';
import { competencySortBy } from '@utils/sort-table-competencies';
import { ACCEPTED_FILE_TYPES, ACCCEPTED_FILE_EXTENSIONS } from './constants';

const DESC_MAX_LENGTH = 2048;

export default {
  components: {
    KModal,
    KEditableTable,
    Dropfield,
    KCharacterCount,
    KForm,
    KFormField,
    KFormSection,
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    modalMode: {
      type: String,
      required: true,
    },
    scopeData: {
      type: Object,
      default: undefined,
    },
  },

  data() {
    return {
      isUploading: false,
      isUpdating: false,
      isEditingLink: false,
      projectName: '',
      projectDescription: '',
      projectScopingDocument: null,
      documentUploadType: 'file',
      competencies: [],
      competenciesReady: false,
      descMaxLength: DESC_MAX_LENGTH,
      urlValidationMessage: '',
      isUrlValid: false,
    };
  },

  watch: {
    showModal(newVal) {
      if (newVal) {
        this.getCompetencies();
      }
    },

    projectScopingDocument: {
      handler(newVal) {
        if (this.isLinkMode && newVal) {
          this.isUrlValid = this.validateUrl(newVal);
        }
      },
      immediate: true,
    },

    scopeData: {
      handler(newVal) {
        if (newVal) {
          this.projectName = newVal.name || '';
          this.projectDescription = newVal.description || '';
          if (this.findDocumentType === 'file') {
            // helps us display correct field in view mode
            this.projectScopingDocument = null;
            // load the data only when it's in draft mode as we don't want to resubmit the same stuff again
            if (newVal?.status === 'draft' || newVal?.status === 'scoping_proposed') {
              this.projectScopingDocument = newVal.scoping_document_link || null;
            }
            this.documentUploadType = 'file';
          } else {
            this.projectScopingDocument = newVal.scoping_document_link || null;
            this.documentUploadType = 'link';
          }
        } else {
          // Reset form when no project scope data is provided
          this.clearFields();
        }
      },
      immediate: true,
    },
  },

  computed: {
    acceptedFileTypes() {
      return ACCEPTED_FILE_TYPES.join(',');
    },
    acceptedFileExtensions() {
      return ACCCEPTED_FILE_EXTENSIONS.join(', ');
    },
    findDocumentType() {
      if (!this.scopeData?.scoping_document_link) return null;
      return this.scopeData.scoping_document_link.split('/')[1] === 'scoping_doc' ? 'file' : 'link';
    },

    hasDocumentToView() {
      return this.findDocumentType === 'file';
    },

    isLinkMode() {
      return this.documentUploadType === 'link';
    },

    checkValidUrlForUpload() {
      if (this.isLinkMode && this.projectScopingDocument) {
        return this.isUrlValid;
      }
      return true;
    },

    modalTitle() {
      if (this.modalMode === 'view' && this.scopeData) {
        return `View ${this.scopeData.name}`;
      }
      return this.modalMode === 'edit' && this.scopeData ? `Edit ${this.scopeData.name}` : 'Scope New Project';
    },
    validData() {
      return Boolean(this.validName && this.validDescription && this.validCompetencies && !this.isEditingLink && this.checkValidUrlForUpload);
    },

    validSubmitForReview() {
      return Boolean(this.validData && !this.scopeData?.scoping_completed);
    },

    statusCanBeDraft() {
      const nonDraftableStatuses = ['in_progress', 'completed', 'scoping_proposed'];
      return Boolean(!this.scopeData?.scoping_completed && !nonDraftableStatuses.includes(this.scopeData?.status));
    },

    validSaveAsDraft() {
      return Boolean(this.validData && this.statusCanBeDraft);
    },

    editingDisabled() {
      return this.modalMode === 'view' || this.scopeData?.scoping_completed || this.isUpdating;
    },

    canDeleteProject() {
      if (!this.scopeData) return false;
      const deletableStatuses = ['draft', 'scoping_proposed'];
      return Boolean(deletableStatuses.includes(this.scopeData.status) && !this.scopeData.scoping_completed);
    },

    validName() {
      return this.projectName.trim() !== '';
    },

    validDescription() {
      return this.projectDescription.trim() !== '';
    },

    validCompetencies() {
      return this.listSelectedCompetencies().length > 0;
    },

    dropfieldMessage() {
      return `<i class="fas fa-cloud-upload-alt" aria-hidden="true"></i>
      <span class="upload-text">Click or drag scoping file here to upload</span>`;
    },

    acceptedFilesInfo() {
      return `<p>Accepted File Types: ${this.acceptedFileExtensions} <br> Maximum File Size: 32MB</p>`;
    },

    tableHeaders() {
      return [
        {
          key: 'checkbox',
          title: '',
          editable: !this.editingDisabled,
          type: 'boolean',
          required: true,
          sortable: false,
        },
        {
          key: 'label',
          title: 'Label',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
          sortBy: competencySortBy,
        },
        {
          key: 'category',
          title: 'Category',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
        {
          key: 'description',
          title: 'Description',
          editable: false,
          sortable: true,
          filterable: true,
          type: 'string',
        },
      ];
    },

    hasUploadedFile() {
      return this.projectScopingDocument && typeof this.projectScopingDocument === 'object';
    },

    initialSortState() {
      return { headerKey: 'label', ascending: true };
    },
  },

  methods: {
    getCompetencies() {
      this.competenciesReady = false;
      this.$Loading.minimal();
      this.$logger.info('Fetching competencies', true);
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/competencies/scores`)
        .then(response => {
          this.competencies = response.data.competencies;
          this.markSelectedCompetencies();
        })
        .catch(error => {
          this.$logger.error('Error fetching competencies', undefined, error);
        }).then(() => {
          this.competenciesReady = true;
          this.$Loading.finish();
        });
    },

    filesChange(file) {
      this.$logger.info('Staged file for upload', { file: file.name });
      this.projectScopingDocument = file;
    },

    deleteProject() {
      this.isUpdating = true;
      this.$logger.info('Deleting project scope', { projectId: this.scopeData.id });
      this.$http.delete(`/api/curriculum/programmes/projects/${this.scopeData.id}`, {
        data: {
          programme_id: this.programmeId,
        },
      })
        .then(() => {
          this.$ktoast.success('Project scope deleted');
          this.$logger.info('Project scope deleted', { projectId: this.scopeData.id });
          this.$emit('close');
          this.$emit('update');
          this.clearFields();
        }).catch(err => {
          this.$logger.autowarn('Error deleting project scope', { projectId: this.scopeData.id }, err);
          this.$ktoast.warning('Error deleting project scope');
        }).finally(() => {
          this.isUpdating = false;
        });
    },

    async uploadScopingDocument(file, projectId) {
      this.isUploading = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_size', parseInt(file.size, 10));
        formData.append('content_type', file.type || 'application/octet-stream');
        formData.append('filename', file.name);
        this.$logger.info('Uploading scoping document', {
          fileName: file.name,
          fileSize: file.size,
          contentType: file.type || 'application/octet-stream',
        });

        const response = await this.$http.post(
          `/api/curriculum/programmes/projects/${projectId}/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        this.$logger.info('File uploaded successfully', {
          fileName: file.name,
          filePath: response.data.resource_name,
        });

        return response.data.resource_name;
      } catch (err) {
        if (this.$http.errIn(err, [400])) {
          this.$ktoast.warning('File validation failed. Please ensure the file is under 32MB.');
        } else {
          this.$ktoast.warning('Error uploading file');
        }
        this.$logger.error('Error uploading file', undefined, err);
        throw err;
      } finally {
        this.isUploading = false;
      }
    },

    async saveNewProject(status) {
      this.isUpdating = true;

      try {
        const payload = {
          programme_id: this.programmeId,
          programme_competency_ids: this.listSelectedCompetencies().map(competency => competency.id),
          name: this.projectName,
          description: this.projectDescription,
          project_type: 'portfolio',
          status,
        };
        this.$logger.info('Saving project scope', { status });
        // first create the project
        const projectResponse = await this.$http.post('/api/curriculum/programmes/projects', payload);
        // check if has scoping document or link either then we update the data
        let updatePayload;
        if (this.hasUploadedFile && projectResponse.data?.project_id) {
          const uploadedResourceName = await this.uploadScopingDocument(this.projectScopingDocument, projectResponse.data.project_id);
          updatePayload = {
            ...payload,
            scoping_document_link: uploadedResourceName,
          };
        } else {
          // otherwise take the scoping document link
          updatePayload = {
            ...payload,
            scoping_document_link: this.projectScopingDocument,
          };
        }
        await this.$http.put(`/api/curriculum/programmes/projects/${projectResponse.data.project_id}`, updatePayload);
        this.$ktoast.success(status === 'draft' ? 'Draft project scope saved' : 'Project scope submitted for review');
        this.$logger.info('Project scope saved', { status });
        this.$emit('close');
        this.$emit('update');
        this.clearFields();
      } catch (err) {
        this.$logger.autowarn('Error saving project scope', { status }, err);
        this.$ktoast.warning(status === 'draft' ? 'Error saving draft project scope' : 'Error submitting project scope for review');
      } finally {
        this.isUpdating = false;
      }
    },

    async editProject(status) {
      this.isUpdating = true;

      try {
        let scopingDocumentLink = this.projectScopingDocument;

        if (this.hasUploadedFile) {
          scopingDocumentLink = await this.uploadScopingDocument(this.projectScopingDocument, this.scopeData.id);
        }

        const payload = {
          programme_id: this.programmeId,
          programme_competency_ids: this.listSelectedCompetencies().map(competency => competency.id),
          name: this.projectName,
          description: this.projectDescription,
          scoping_document_link: scopingDocumentLink,
          project_type: 'portfolio',
          status,
        };

        this.$logger.info('Updating project scope', { projectId: this.scopeData.id, status });
        await this.$http.put(`/api/curriculum/programmes/projects/${this.scopeData.id}`, payload);
        this.$logger.info('Project scope updated', { projectId: this.scopeData.id, status });
        this.$emit('close');
        this.$emit('update');
        this.clearFields();
      } catch (err) {
        this.$logger.autowarn('Error saving project scope', { projectId: this.scopeData.id, status }, err);
        this.$ktoast.warning(status === 'draft' ? 'Error updating draft project scope' : 'Error submitting project scope for review');
      } finally {
        this.isUpdating = false;
      }
    },

    confirmDeleteProject() {
      this.$ktoast.confirmToast(`Are you sure you want to delete this project: <strong>${this.scopeData.name}</strong>?`, 'warning', () => {
        this.deleteProject();
      });
    },

    saveOrEditDraft() {
      if (this.modalMode === 'edit') {
        this.editProject('draft');
      } else {
        this.saveNewProject('draft');
      }
    },

    submitForReview() {
      if (this.modalMode === 'edit') {
        this.editProject('scoping_proposed');
      } else {
        this.saveNewProject('scoping_proposed');
      }
    },

    clearFields() {
      // don't reset document uploadType here would like to maintain it
      this.projectName = '';
      this.projectDescription = '';
      this.projectScopingDocument = null;
      this.isUrlValid = false;
      this.urlValidationMessage = '';
      this.isEditingLink = false;
      this.removeUploadedFile();
    },

    removeUploadedFile() {
      if (this.$refs.scopeUploadField) {
        this.$refs.scopeUploadField.reset();
      }
    },

    onClose() {
      this.clearFields();
      this.$emit('close');
    },

    markSelectedCompetencies() {
      if (!this.competencies || !this.scopeData?.competencies) {
        return;
      }
      const selectedCompetencyIds = this.scopeData.competencies.map(c => c.id);
      this.competencies.forEach((competency, index) => {
        this.competencies[index].checkbox = selectedCompetencyIds.includes(competency.id);
      });
    },

    listSelectedCompetencies() {
      return this.competencies.filter(competency => competency.checkbox);
    },

    setDocumentUploadMode(mode) {
      if (mode === 'link') {
        this.isEditingLink = false;
        this.documentUploadType = mode;
        this.isUrlValid = false;
      } else {
        this.isEditingLink = false;
        this.documentUploadType = mode;
      }
      this.projectScopingDocument = null;
      this.urlValidationMessage = '';
      this.removeUploadedFile();
    },

    downloadScopingDocument() {
      const fileName = this.scopeData?.scoping_document_link.split('/').at(-1);
      if (!fileName) {
        this.$logger.error('No filename found in scoping document link');
        this.$ktoast.warning('Error downloading document');
        return;
      }
      const downloadUrl = `/api/curriculum/programmes/projects/${this.scopeData.id}/doc/${fileName}`;
      this.$logger.info('Downloading scoping document', {
        projectId: this.scopeData.id,
        fileName,
      });
      window.open(downloadUrl, '_blank');
    },

    validateUrl(url) {
      const { isValid, message } = this.$validator.urlValidator(url);
      this.urlValidationMessage = message;
      return isValid;
    },
  },
};
</script>
